import { Permission } from '@bookora/shared';

import type { TourDef } from './types';

/** `/import/photo/:jobId` — the review of one «ثبت اطلاعات از روی عکس» job. */
export function isImportJobPath(pathname: string): boolean {
  return /^\/import\/photo\/[^/]+$/.test(pathname);
}

/**
 * «اولین بازبینی»: offered on the first READY review. Every step after the
 * first two is decided by what the page renders:
 *
 *  · `flagged-cell` skips itself when nothing on the page is flagged;
 *  · `unit` exists only for kinds that carry amounts;
 *  · the kind steps — `history` (TREATMENTS), `count-diff` (STOCK_COUNT),
 *    `group` (PATIENTS) — are mutually exclusive: each kind renders only its
 *    own anchor. A kind not yet switched on never renders its step.
 *
 * Eight steps are defined and at most five are SHOWN (the owner's reading of
 * «≤ 5», critic C10): the runner trims to `maxSteps` over the anchors that are
 * actually on the page, keeping this priority. `unit` ranks with the kind
 * steps — for a kind that has both, both are kept and `viewer` is the one to
 * go — and ahead of them, so the few kinds with a unit selector never lose it.
 *
 * Version 2 (critic C9: one bump per release, and only when the step set
 * changes): wave 2 made the three kind steps real — the group option on
 * PATIENTS now, past visits and the stock count as their kinds are switched
 * on — so the review is re-offered once as «به‌روز شده».
 */
export const AI_IMPORT_REVIEW_TOUR: TourDef = {
  key: 'ai-import-review',
  version: 2,
  surface: 'page',
  title: 'بازبینی اطلاعات عکس',
  hubDescription: 'رفتن به موردهای نیازمند بررسی، مقایسه با عکس، واحد مبلغ‌ها و ثبت نهایی.',
  offer: { body: 'قبل از ثبت، در چند قدم کوتاه نشونتون می‌دیم چه چیزهایی رو بازبینی کنید.' },
  route: { path: '/import/photo', match: isImportJobPath, context: 'import-job' },
  requiresFeature: 'aiImport',
  autoOffer: true,
  maxSteps: 5,
  minSteps: 2,
  // C10: ['next-flag', 'commit', 'flagged-cell', <kind step>, 'viewer'], where
  // the kind step is `history` | `count-diff` | `group` | `unit` (mutually
  // exclusive by anchor, except `unit`, which a TREATMENTS or priced
  // STOCK_COUNT review also shows — then `viewer` is the one to go).
  priority: [
    'next-flag',
    'commit',
    'flagged-cell',
    'unit',
    'history',
    'count-diff',
    'group',
    'viewer',
  ],
  steps: [
    {
      id: 'next-flag',
      anchor: 'import.review.next-flag',
      placement: 'bottom',
      title: 'موردهای نیازمند بررسی',
      body: 'اطلاعاتی که ممکنه اشتباه خوانده شده باشن مشخص شده‌اند. با این دکمه، اون‌ها رو یکی‌یکی با عکس مقایسه کنید.',
    },
    {
      id: 'viewer',
      anchor: 'import.viewer',
      placement: 'auto',
      title: 'عکس کنار جدول',
      // image-viewer.tsx: a band captioned «محل تقریبی», zoom and pan.
      body: 'ردیفی که انتخاب می‌کنید روی عکس با نوار «محل تقریبی» نشان داده می‌شه. برای دیدن جزئیات، عکس رو بزرگ کنید.',
    },
    {
      id: 'flagged-cell',
      anchor: 'import.review.flagged-cell',
      placement: 'auto',
      title: 'مقایسه با عکس',
      body: 'مقدار رو با همان بخش عکس مقایسه کنید. اگر درسته تأییدش کنید، وگرنه همین‌جا اصلاحش کنید.',
    },
    {
      id: 'unit',
      anchor: 'import.review.unit',
      placement: 'bottom',
      title: 'تومان یا ریال؟',
      body: 'واحد مبلغ‌های عکس رو اینجا مشخص کنید. همهٔ مبلغ‌ها با همین واحد خوانده و در بوکورا به تومان نمایش داده می‌شن.',
    },
    {
      // TREATMENTS (past visits). Not «اثر جانبی»: in a clinic that reads
      // as a medical side effect.
      id: 'history',
      anchor: 'import.review.history',
      placement: 'bottom',
      // Not «فقط در پرونده»: the paid amount does count in the clinic's
      // revenue (owner decision); what never happens is an SMS or a stock
      // draw.
      title: 'فقط سابقه، بدون پیامک',
      body: 'این جلسات درمان به‌عنوان سابقه در پرونده ثبت می‌شن؛ پیامکی ارسال نمی‌شه و از انبار چیزی کم نمی‌شه. اگر همهٔ برگه‌ها مربوط به یک بیمار هستن، همون بیمار رو اینجا انتخاب کنید.',
    },
    {
      // STOCK_COUNT: the first row's diff cell.
      id: 'count-diff',
      anchor: 'import.review.count-diff',
      placement: 'bottom',
      title: 'موجودی سیستم و شمارش شما',
      body: 'کنار هر ردیف، موجودی ثبت‌شده در بوکورا و اختلافش با شمارش شما نمایش داده شده. اختلاف‌های بزرگ رو با عکس مقایسه کنید.',
    },
    {
      // PATIENTS: the «گروه» option card. Making a group needs the CRM keys
      // and tagging the patients needs patient.edit.
      id: 'group',
      anchor: 'import.review.group',
      placement: 'bottom',
      requires: {
        all: [Permission.CRM_VIEW_LEADS, Permission.CRM_MANAGE_LEADS, Permission.PATIENT_EDIT],
      },
      title: 'افزودن به گروه',
      body: 'اگر این فهرست برای پیامک یا پیگیریه، براش گروه بسازید تا بعداً بتونید به همهٔ اعضا پیامک بفرستید.',
    },
    {
      id: 'commit',
      anchor: 'import.review.commit',
      placement: 'bottom-end',
      title: 'ثبت نهایی',
      body: 'تا این دکمه رو نزنید چیزی در پرونده‌ها و حساب‌های کلینیک ثبت نمی‌شه.',
    },
  ],
};
